import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useParams } from "react-router-dom";
import { BACKEND_URL } from "@/constants/api";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/layouts/PageHeader";
import { LucideUser, LucidePencil, LucideEye, LucideChevronLeft, LucideUsers } from "lucide-react";
import { StudentReportList } from "@/components/entities/StudentReportList";
import { StudentListSizeToggle } from "@/components/entities/StudentListSizeToggle";
import { useStudentListSize } from "@/hooks/useStudentListSize";
import { formatGrade } from "@/utils/formatGrade";

const REPORTS_PAGE_SIZE = 10;

export default function SharedReportFolder() {
  const { sharedReportFolderId } = useParams();
  const { getToken } = useAuth();

  const [sharedReportFolder, setSharedReportFolder] = useState(null);
  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [reportsPage, setReportsPage] = useState(0);
  const [listSize, setListSize] = useStudentListSize();

  useEffect(() => { setReportsPage(0); }, [sharedReportFolderId]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchSharedReportFolder = async () => {
      try {
        const token = await getToken();
        const params = new URLSearchParams({ page: reportsPage, limit: REPORTS_PAGE_SIZE });
        const res = await fetch(`${BACKEND_URL}api/shared-report-folders/${sharedReportFolderId}?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch shared Report Folder");
        const data = await res.json();
        setSharedReportFolder(data);
        setReports(data.reports || []);
        setTotalReports(data.totalReports ?? 0);
        setFetchError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching shared Report Folder:", err);
        setFetchError("Could not load this shared Report Folder. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    fetchSharedReportFolder();
    return () => controller.abort();
  }, [sharedReportFolderId, getToken, reportsPage]);

  if (loading) return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      <div className="h-4 w-40 bg-muted animate-pulse rounded mb-6" />
      <div className="h-10 w-2/3 bg-muted animate-pulse rounded mb-5" />
      <div className="h-7 w-56 bg-muted animate-pulse rounded mb-2" />
      <div className="h-4 w-40 bg-muted animate-pulse rounded mb-8" />
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    </div>
  );
  if (fetchError) return <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6"><div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fetchError}</div></div>;
  if (!sharedReportFolder) return <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6 text-muted-foreground">Shared Report Folder not found.</div>;

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      <Link
        to="/shared-report-folders"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <LucideChevronLeft size={14} />
        Shared Report Folders
      </Link>

      <div className="mb-5 flex items-center gap-2.5 rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-sm text-warning-fg">
        <LucideUsers size={15} className="shrink-0" />
        <span>
          Shared with you{sharedReportFolder.sharedBy?.name ? <> by <strong>{sharedReportFolder.sharedBy.name}</strong></> : ""}
        </span>
        <Badge variant={sharedReportFolder.permission === "edit" ? "info" : "outline"}>
          {sharedReportFolder.permission === "edit" ? <LucidePencil size={10} /> : <LucideEye size={10} />}
          {sharedReportFolder.permission === "edit" ? "Can edit" : "View only - editing is disabled"}
        </Badge>
      </div>

      <PageHeader
        title={sharedReportFolder.name}
        subtitle={
          (sharedReportFolder.grade || sharedReportFolder.schoolYear)
            ? [formatGrade(sharedReportFolder.grade), sharedReportFolder.schoolYear].filter(Boolean).join(" · ")
            : undefined
        }
      />

      {reports.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <LucideUser size={40} className="mx-auto mb-3 opacity-40" />
          <p>No report cards in this Report Folder yet.</p>
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <StudentListSizeToggle size={listSize} onChange={setListSize} />
          </div>

          <StudentReportList
            reports={reports}
            size={listSize}
            getLinkPath={(card) => `/shared-report-folders/${sharedReportFolderId}/reports/${card._id}`}
            linkState={{ permission: sharedReportFolder.permission }}
            totalPages={Math.ceil(totalReports / REPORTS_PAGE_SIZE)}
            currentPage={reportsPage}
            onPageChange={setReportsPage}
            canEdit={false}
            showMenu={false}
          />
        </>
      )}
    </div>
  );
}
